import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Grid2 from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import { linearProgressClasses } from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ShareIcon2 from '../../../svg/ShareIcon2';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoIcon from '@mui/icons-material/Info';
import { useRef, useState } from "react";
import Lottie from "lottie-react";
import LoadingFace from '../../../assets/animations/LoadingFace.json';
import AristoWave from '../../../assets/animations/AristoWave.json';
import FriendDetail from "./friendDetail";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../hooks/useAuth";
import { fetchReferrals, handleSendReferralEmail, isValidEmail } from "../../../utils/apis/referrals";
import { toast } from "react-toastify";
import InviteSentIcon from "../../../svg/ReferralIcons/InviteSentIcon";
import InvitePendingIcon from "../../../svg/ReferralIcons/InvitePendingIcon";
import AccountsCreatedIcon from "../../../svg/ReferralIcons/AccountsCreatedIcon";
import CloseIcon from "../../../svg/CloseIcon";
import { ReactComponent as FacebookIcon } from '../../../svg/Shareicons/facebook.svg';
import { ReactComponent as TwitterIcon } from '../../../svg/Shareicons/twitter.svg';
import { ReactComponent as LinkedinIcon } from '../../../svg/Shareicons/linkedin.svg';
import { ReactComponent as WhatsappIcon } from '../../../svg/Shareicons/whatsapp.svg';
import { ReactComponent as TelegramIcon } from '../../../svg/Shareicons/telegram.svg';
import { ReactComponent as RedditIcon } from '../../../svg/Shareicons/reddit.svg';
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  TelegramShareButton,
  RedditShareButton,
} from 'react-share';
import MetaTags from "../../../component/MetaTags";
import { ReferralFAQs } from "./referral_faqs";

export interface InviteStatusType {
  status: 'pending' | 'account_created' | 'persona_created';
  userName?: string;
}
export interface ReferralDataType {
  referralCode: string;
  data: {[key: string]: InviteStatusType};
}


const ReferralsPage: React.FC = () => {
  const [ showDetails, setShowDetails ] = useState<boolean>(false);
  const [ shareInvite, setShareInvite ] = useState<boolean>(false);
  const [ isInviteEnabled, setIsInviteEnabled ] = useState<boolean>(false);
  const inputRef = useRef(null);
  // for use inside modal
  const inputRef2 = useRef(null);
  const [ isInviteEnabled2, setIsInviteEnabled2 ] = useState<boolean>(false);

  const { authUser, authLoading } = useAuth();

  const {
    data: referralInfo,
    isLoading: referralLoading,
    refetch,
  } = useQuery({
    queryKey: ['referrals', authUser?.uid],
    queryFn: async () => {
      if (authLoading || !authUser) {
        return {} as ReferralDataType;
      }
      return fetchReferrals(authUser?.uid)
        .then((resp) => {
          if (resp.ok) {
            return resp.json();
          }
        })
        .then((data: ReferralDataType) => {
          // console.log(data)
          return data;
        })
        .catch((error) => {
          console.log(error);
          return {} as ReferralDataType;
        })
    },
  })

  const referralLink = window.origin + `?createAccount=true&referralCode=${referralInfo?.referralCode}`;
  const referralBody = "I just joined the Soopra.ai Persona Creation Contest  — where creators and experts build their own AI twins. Why I’m excited: ✅ I can create an AI persona in my own voice & style ✅ Train it with my stories, expertise, and content ✅ Engage with followers 24/7 (even while I’m offline) ✅  And yes… compete for $2,000 in prizes 🎉 If you’ve ever wanted to scale your presence, this is the moment. Every new persona makes the contest more exciting — and you could be one of the winners too. Who’s in? #SoopraAI #DigitalTwin #ExpertEconomy";

  const handleShareClick = () => {
    setShareInvite(true);
  }

  const handleCopyClick = () => {
    if (!referralInfo || !referralInfo.referralCode) return;
    let copyLink = window.origin + `?createAccount=true&referralCode=${referralInfo.referralCode}`
    navigator?.clipboard?.writeText?.(copyLink);
    toast.success("Copied!")
  }

  return (
    <Box
      width='100%'
      height='100%'
      display='flex'
      justifyContent='center'
    >
      <MetaTags
        title="Invite Friends and Earn Points | Soopra Referrals"
        description="Turn your network into your advantage! Climb the leaderboard when you refer friends to Soopra."
        image='https://soopra.ai/images/soopra_referral_rewards_preview.png'
      />
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: '100%',
          flexDirection: 'column',
          px: 2.5,
          py: 3,
          gap: 4,
          maxWidth: 1200,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            gap: 1.5,
          }}
        >
          <Box display='flex' flexDirection='column'>
            <Typography variant='h5' pb={0.5} sx={{display: 'flex', alignItems: 'center'}}>
              Invite Friends. Earn Points. Climb the Leaderboard.
              <InfoIcon
                sx={{
                  ml: 0.5,
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setShowDetails(true);
                }}
              />
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Turn your network into your advantage! Share Soopra.ai with your friends, fans, followers and increase your reach and engagement. On top of it, when they create their AI persona, you’ll earn 10 bonus points per referral. Stack up points and boost your Soopra Score.
            </Typography>
          </Box>

          <Box display='flex' sx={{gap: 1.5}}>
            <Button
              variant="contained"
              startIcon={<ShareIcon2 stroke='inherit'/>}
              sx={{stroke: (theme) => theme.palette.primary.contrastText}}
              onClick={() => {
                handleShareClick();
              }}
            >
              Share Invites
            </Button>
            <Button
              variant='outlined'
              color='secondary'
              startIcon={<ContentCopyIcon />}
              onClick={() => {
                handleCopyClick();
              }}
            >
              Copy Link
            </Button>
          </Box>
        </Box>

        <Box
          display='flex'
          flexDirection='column'
          pb={1}
          gap={2}
        >
          <Typography variant='h7'>
              My Progress
          </Typography>

          <Box display='flex' alignItems='center' gap={3}>
            <Box
              sx={{
                display: {xs: 'none', md: 'block'} // hide if smaller than 900
              }}
            >
              <Lottie
                animationData={AristoWave}
                loop={true}
                style={{
                  width: 211,
                  height: 203,
                }}
              />
            </Box>

            <Box
              display='flex'
              flexDirection='column'
              flexGrow={1}
              gap={2}
            >
              <Grid2 container spacing={1.5}>
                <Grid2
                  size={4}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 1.5,
                    px: 2,
                    borderRadius: (theme) => theme.shape.borderRadiusMd,
                    background: (theme) => theme.palette.background["paper-elevation-10"]
                  }}
                >
                  <InviteSentIcon />
                  <Typography variant='h5'>
                    {(!!referralInfo && !!referralInfo.data) ? Object.keys(referralInfo.data).length : 0}
                  </Typography>
                  <Typography variant='body2' color='text.secondary' textAlign='center'>
                    Invites Sent
                  </Typography>
                </Grid2>

                <Grid2
                  size={4}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 1.5,
                    px: 2,
                    borderRadius: (theme) => theme.shape.borderRadiusMd,
                    background: (theme) => theme.palette.background["paper-elevation-10"]
                  }}
                >
                  <InvitePendingIcon />
                  <Typography variant='h5'>
                  {(!!referralInfo && !!referralInfo.data) ?
                    Object.values(referralInfo.data).filter((val) => val.status === 'pending').length
                    : 0
                  }
                  </Typography>
                  <Typography variant='body2' color='text.secondary' textAlign='center'>
                    Invites Pending
                  </Typography>
                </Grid2>

                <Grid2
                  size={4}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 1.5,
                    px: 2,
                    borderRadius: (theme) => theme.shape.borderRadiusMd,
                    background: (theme) => theme.palette.background["paper-elevation-10"]
                  }}
                >
                  <AccountsCreatedIcon />
                  <Typography variant='h5'>
                  {(!!referralInfo && !!referralInfo.data) ?
                    Object.values(referralInfo.data).filter((val) => val.status === 'account_created').length
                    : 0
                  }
                  </Typography>
                  <Typography variant='body2' color='text.secondary' textAlign='center'>
                    Accounts Created
                  </Typography>
                </Grid2>
              </Grid2>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  width: '100%',
                }}
              >
                <Typography variant='body2' color='text.secondary'>
                  AI Personas Created
                </Typography>
                <LinearProgress
                  variant='determinate'
                  value={(!!referralInfo && !!referralInfo.data) ?
                    Object.values(referralInfo.data).filter((val) => val.status === 'persona_created').length/5*100
                    : 0
                  } // value between 0-100
                  sx={{
                    height: 9,
                    borderRadius: (theme) => theme.shape.borderRadiusLg,
                    backgroundColor: (theme) => theme.palette.primary._states.focus,
                    [`& .${linearProgressClasses.bar}`]: {
                      backgroundColor: '#0810FF',
                      borderRadius: 30,
                    }
                  }}
                />
                <Box display='flex' justifyContent='space-between' >
                  {[0,1,2,3,4,5].map((_, index) => (
                    <Typography variant='body2' color='text.secondary' key={index}>
                      {index}
                    </Typography>
                    ))
                  }
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              alignItems: {xs: 'flex-start', sm: 'center'},
              flexDirection: {xs: 'column', sm: 'row'},
            }}
          >
            <Typography variant='h7' mr={3.75} mb={{xs: 1, sm: 0}}>
              My Invites
            </Typography>
            <Box
              flexGrow={1}
              display='flex'
              width={{xs: '100%', sm: 'auto'}}
            >
              <TextField
                inputRef={inputRef}
                size='small'
                sx={{
                  flexGrow: 1,
                }}
                placeholder='friend@email.com'
                onChange={(e) => {
                  if (isValidEmail(e.target.value) && !isInviteEnabled) {
                    setIsInviteEnabled(true);
                  } else if (!isValidEmail(e.target.value) && isInviteEnabled){
                    setIsInviteEnabled(false);
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleSendReferralEmail(authUser, inputRef?.current?.value, refetch, toast, inputRef);
                  }
                }}
              />
              <Button
                variant='contained'
                sx={{
                  ml: 1.5,
                }}
                disabled={!isInviteEnabled}
                onClick={() => {
                  handleSendReferralEmail(authUser, inputRef?.current?.value, refetch, toast, inputRef);
                }}
              >
                + Invite
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              width: '100%',
              flexDirection: 'column',
              gap: 1,
              pb: 2,
            }}
          >
          {
            referralLoading ?
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                '& path': {
                  fill: (theme) => theme.palette.primary.main
                }
              }}
            >
              <Lottie
                animationData={LoadingFace}
                loop={true}
                style={{
                  width: '100px',
                  height: '100px',
                }}
              />
            </Box> :
            (referralInfo && Object.entries(referralInfo?.data).length) ? (
              Object.entries(referralInfo?.data).map(([email, inviteStatus], index) => (
                <FriendDetail
                  key={index}
                  email={email}
                  inviteStatus={inviteStatus}
                />
              ))
            ) : <Box
              width='100%'
              sx={{
                borderRadius: (theme) => theme.shape.borderRadiusMd,
                px: 1.5,
                py: 1,
                background: (theme) => theme.palette.background["paper-elevation-7"],
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Typography variant='body2' color='text.secondary'>
                No invites sent, yet!
              </Typography>
            </Box>
          }
          </Box>
        </Box>
      </Box>
      <Dialog
        open={showDetails}
        onClose={() => {
          setShowDetails(false);
        }}
        maxWidth='sm'
        slotProps={{
          paper: {
            sx: {
              m: 0,
              maxHeight: 'none',
              borderRadius: (theme) => theme.shape.borderRadiusMd,
            }
          }
        }}
      >
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  width: '100%',
                }}
              >
                <Typography variant='h5' sx={{flexGrow: 1}}>
                  Referral Info
                </Typography>
                <IconButton
                  onClick={() => {
                    setShowDetails(false);
                  }}
                >
                  <CloseIcon color='text.primary' strokeWidth={1.5}/>
                </IconButton>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                width: '100%',
              }}
            >
              {
                ReferralFAQs.map((item, ind) => (
                  <Box
                    key={`referral-faq-${ind}`}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      width: '100%',
                    }}
                  >
                    <Typography variant='h6'>
                      {item.question}
                    </Typography>
                    <Typography variant='body1'>
                      {item.answer}
                    </Typography>
                    {
                      item.bullets &&
                      <List
                        sx={{
                          listStyleType: 'disc',
                          py: 0,
                        }}
                      >
                      {
                        item.bullets.map(((bullet, ind2) => (
                          <ListItem
                            key={`referral-faq-${ind}-${ind2}`}
                            sx={{py: 0,}}
                          >
                            <Typography variant='body1' >
                              {bullet}
                            </Typography>
                          </ListItem>
                        )))
                      }
                      </List>
                    }
                  </Box>
                ))
              }
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog
        open={shareInvite}
        onClose={(e: React.SyntheticEvent) => {
          e.stopPropagation();
          setShareInvite(false);
        }}
        fullWidth
        maxWidth="xs"
        slotProps={{
          paper: {
          sx: {
            borderRadius: 4,
            padding: 2,
            backdropFilter: 'blur(8px)',
            backgroundColor: theme => theme.palette.background["container-0"],
          },
          onClick: (e) => e.stopPropagation(),
        }}}
      >
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{flexGrow: 1}}>
              Share Referral Link
            </Typography>
            <IconButton
              onClick={() => {
                setShareInvite(false);
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>


          <Typography variant="body2" color="text.secondary" mb={1} sx={{textAlign: 'left',}}>
            Social
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
            <Stack direction="column" alignItems="center" spacing={0.5}>
              <FacebookShareButton url={referralLink} onClick={(e) => e.stopPropagation()}>
                <IconButton sx={{cursor: 'pointer', '&:hover': {transform: 'scale(1.1)',borderColor: 'rgba(0,0,0,0.3)'},}}>
                  <FacebookIcon width={40} height={40} />
                </IconButton>
              </FacebookShareButton>
              <Typography variant="caption" color="text.secondary">
                Facebook
              </Typography>
            </Stack>

            <Stack direction="column" alignItems="center" spacing={0.5}>
              <TwitterShareButton
                url={referralLink}
                title={referralBody}
                onClick={(e) => e.stopPropagation()}
              >
                <IconButton>
                  <TwitterIcon width={40} height={40} />
                </IconButton>
              </TwitterShareButton>
              <Typography variant="caption" color="text.secondary">
                X
              </Typography>
            </Stack>

            <Stack direction="column" alignItems="center" spacing={0.5}>
              <LinkedinShareButton url={referralLink} source={referralLink} title={referralBody} summary={referralBody} onClick={(e) => e.stopPropagation()}>
              <IconButton sx={{cursor: 'pointer', '&:hover': {transform: 'scale(1.1)',borderColor: 'rgba(0,0,0,0.3)'},}}>
                <LinkedinIcon width={40} height={40} />
              </IconButton>
              </LinkedinShareButton>
              <Typography variant="caption" color="text.secondary">
                Linkedin
              </Typography>
            </Stack>

            <Stack direction="column" alignItems="center" spacing={0.5}>
              <WhatsappShareButton url={referralLink} title={referralBody} onClick={(e) => e.stopPropagation()}>
                <IconButton sx={{cursor: 'pointer', '&:hover': {transform: 'scale(1.1)',borderColor: 'rgba(0,0,0,0.3)'},}}>
                  <WhatsappIcon width={40} height={40} />
                </IconButton>
              </WhatsappShareButton>
              <Typography variant="caption" color="text.secondary">
                Whatsapp
              </Typography>
            </Stack>

            <Stack direction="column" alignItems="center" spacing={0.5}>
              <TelegramShareButton url={referralLink} title={referralBody} onClick={(e) => e.stopPropagation()}>
                <IconButton sx={{cursor: 'pointer', '&:hover': {transform: 'scale(1.1)',borderColor: 'rgba(0,0,0,0.3)'},}}>
                  <TelegramIcon width={40} height={40} />
                </IconButton>
              </TelegramShareButton>
              <Typography variant="caption" color="text.secondary">
                Telegram
              </Typography>
            </Stack>

            <Stack direction="column" alignItems="center" spacing={0.5}>
              <RedditShareButton url={referralLink} title={referralBody} onClick={(e) => e.stopPropagation()}>
                <IconButton sx={{cursor: 'pointer', '&:hover': {transform: 'scale(1.1)',borderColor: 'rgba(0,0,0,0.3)'},}}>
                  <RedditIcon width={40} height={40} />
                </IconButton>
              </RedditShareButton>
              <Typography variant="caption" color="text.secondary">
                Reddit
              </Typography>
            </Stack>
          </Stack>

          <Box mt={3}>
            <Typography variant="body2" color="text.secondary" mb={1} sx={{textAlign: 'left',}}>
              Email
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                flexGrow={1}
                display='flex'
                width={{xs: '100%', sm: 'auto'}}
              >
                <TextField
                  inputRef={inputRef2}
                  size='small'
                  sx={{
                    flexGrow: 1,
                  }}
                  placeholder='friend@email.com'
                  onChange={(e) => {
                    if (isValidEmail(e.target.value) && !isInviteEnabled2) {
                      setIsInviteEnabled2(true);
                    } else if (!isValidEmail(e.target.value) && isInviteEnabled2){
                      setIsInviteEnabled2(false);
                    }
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleSendReferralEmail(authUser, inputRef2?.current?.value, refetch, toast, inputRef2)
                      .finally(() => {
                        setShareInvite(false);
                      });
                    }
                  }}
                />
                <Button
                  variant='contained'
                  sx={{
                    ml: 1.5,
                  }}
                  disabled={!isInviteEnabled2}
                  onClick={() => {
                    handleSendReferralEmail(authUser, inputRef2?.current?.value, refetch, toast, inputRef2)
                    .finally(() => {
                      setShareInvite(false);
                    })
                  }}
                >
                  + Invite
                </Button>
              </Box>
            </Stack>
          </Box>

          <Box mt={3}>
            <Typography variant="body2" color="text.secondary" mb={1} sx={{textAlign: 'left',}}>
              Link
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                onClick={(e) => e.stopPropagation()}
                sx={{
                  bgcolor: '#f5f5f5',
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  flexGrow: 1,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {`${referralLink}`}
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(`${referralLink}`);
                  setShareInvite(false);
                }}
              >
                Copy
              </Button>
            </Stack>
          </Box>
        </Box>
      </Dialog>
    </Box>
  )
}

export default ReferralsPage;
